import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { controlClass } from './Field';
import { Popover } from './Popover';

interface DateFieldProps {
  id?: string;
  name?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Compact variant for table cells: 32px tall, no clear button. */
  compact?: boolean;
  /** Secondary text after the date, e.g. a time. */
  detail?: string;
  className?: string;
  'aria-label'?: string;
}

const SV_DATE = new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
const SV_DATE_SHORT = new Intl.DateTimeFormat('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
const SV_DATE_COMPACT = new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short' });
const SV_WEEKDAY = new Intl.DateTimeFormat('sv-SE', { weekday: 'short' });
const SV_MONTH = new Intl.DateTimeFormat('sv-SE', { month: 'long', year: 'numeric' });
const WEEKDAYS = ['Må', 'Ti', 'On', 'To', 'Fr', 'Lö', 'Sö'];

export const formatSvDate = (d: Date | null): string => (d ? SV_DATE.format(d).replace('.', '') : '');
export const formatSvDateShort = (d: Date | null): string => (d ? SV_DATE_SHORT.format(d).replace('.', '') : '');

/** YYYY-MM-DD in local time; the value the hidden form input carries. */
const toInputValue = (d: Date | null): string => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const parseInput = (v: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
};
const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * A date control that reads in Swedish ("13 okt 2026") in every browser and
 * opens its own calendar popover on click, so it does not depend on the
 * browser's native date picker (Safari does not open one for a covered input).
 * Keyboard: Enter or Space opens the calendar, arrows move by day and week,
 * Escape closes; the open panel also has a YYYY-MM-DD field for typing.
 */
export function DateField({ id, name, value, onChange, placeholder = 'Välj datum', disabled, compact = false, detail, className = '', ...aria }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [cursor, setCursor] = useState<Date>(() => value ?? new Date());
  const [typed, setTyped] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setCursor(value ?? new Date());
      setTyped(toInputValue(value));
    }
  }, [open, value]);

  const label = compact ? (value ? SV_DATE_COMPACT.format(value).replace('.', '') : '') : formatSvDate(value);
  const weekday = compact && value ? SV_WEEKDAY.format(value).replace('.', '') : '';

  const pick = (d: Date | null) => { onChange(d); setOpen(false); anchorRef.current?.focus(); };

  // Month grid, Monday first, six rows so the panel never changes height.
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first); start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const days: Date[] = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = new Date();

  const onGridKey = (e: React.KeyboardEvent) => {
    const step: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in step) {
      e.preventDefault();
      const next = new Date(cursor); next.setDate(cursor.getDate() + step[e.key]); setCursor(next);
      requestAnimationFrame(() => gridRef.current?.querySelector<HTMLButtonElement>('[data-cursor="true"]')?.focus());
    } else if (e.key === 'Enter') { e.preventDefault(); pick(cursor); }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden form value so validation code can still find the field by name. */}
      <input type="hidden" id={id} name={name} value={toInputValue(value)} readOnly />
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        aria-label={aria['aria-label'] ? `${aria['aria-label']}${value ? `: ${formatSvDate(value)}` : ''}` : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`${controlClass} ${compact ? 'h-[32px] px-2 text-[13px]' : ''} flex items-center gap-2 text-left cursor-pointer ${value && !compact ? 'pr-9' : ''}`}
      >
        {!compact && <Calendar size={16} className="text-[var(--text-secondary)] flex-shrink-0" aria-hidden="true" />}
        <span className={`flex-1 truncate tabular-nums ${value ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
          {weekday && <span className="hidden min-[1400px]:inline">{weekday} </span>}
          {label || placeholder}
          {value && detail && <span className="text-[var(--text-secondary)]"> · {detail}</span>}
        </span>
      </button>
      {value && !compact && !disabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(null); }}
          aria-label="Rensa datum"
          className="absolute right-0.5 top-1/2 -translate-y-1/2 h-[32px] w-[32px] flex items-center justify-center rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]"
        >
          <X size={14} />
        </button>
      )}

      <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} width={300} aria-label="Välj datum">
        <div className="p-3" onKeyDown={onGridKey}>
          <div className="flex items-center justify-between mb-2">
            <button type="button" aria-label="Föregående månad" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="h-[32px] w-[32px] flex items-center justify-center rounded-ios-control text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"><ChevronLeft size={16} /></button>
            <span className="text-sm font-semibold text-[var(--text-primary)] capitalize" aria-live="polite">{SV_MONTH.format(cursor)}</span>
            <button type="button" aria-label="Nästa månad" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="h-[32px] w-[32px] flex items-center justify-center rounded-ios-control text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 text-center text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)] mb-1" aria-hidden="true">
            {WEEKDAYS.map(d => <div key={d} className="h-6 leading-6">{d}</div>)}
          </div>
          <div ref={gridRef} role="grid" aria-label="Kalender" className="grid grid-cols-7 gap-0.5">
            {days.map(d => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const selected = sameDay(d, value);
              const isToday = sameDay(d, today);
              const isCursor = sameDay(d, cursor);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  aria-label={formatSvDateShort(d)}
                  data-cursor={isCursor ? 'true' : undefined}
                  tabIndex={isCursor ? 0 : -1}
                  onClick={() => pick(d)}
                  className={`h-[34px] rounded-ios-control text-[13px] tabular-nums transition-colors ${
                    selected ? 'bg-accent-600 text-white font-semibold'
                    : isToday ? 'text-accent-700 dark:text-accent-300 font-semibold hover:bg-[var(--bg-surface-alt)]'
                    : inMonth ? 'text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
                    : 'text-[var(--text-tertiary)] hover:bg-[var(--bg-surface-alt)]'}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const d = parseInput(typed); if (d) pick(d); } e.stopPropagation(); }}
              placeholder="ÅÅÅÅ-MM-DD"
              aria-label="Skriv datum"
              className={`${controlClass} h-[32px] text-[13px] flex-1 tabular-nums`}
            />
            <button type="button" onClick={() => pick(today)} className="h-[32px] px-2.5 rounded-ios-control text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]">Idag</button>
            {value && <button type="button" onClick={() => pick(null)} className="h-[32px] px-2.5 rounded-ios-control text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]">Rensa</button>}
          </div>
        </div>
      </Popover>
    </div>
  );
}

export default DateField;
